
import { defineComponent, ref, reactive, watch, onMounted, computed } from "vue";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { MenuComponent } from "@/assets/ts/components/MenuComponent";
import { useLazyQuery } from "@vue/apollo-composable";
import { perjawatanTransList } from "@/apollo/perkhidmatan/perjawatan";
import KTDatatableBackend from "@/components/kt-datatable/KTDatatableBackend.vue";
import moment from "moment";
import { orderBy } from "lodash-es";

interface BahcawJanm {
  __typename: "BahcawJanm";
  nama: string;
}

interface Perjawatan {
  __typename: "Perjawatan";
  id: string;
  kumpulan_perjawatan: string;
  kod_skim_perkhidmatan: string;
  gred_sspa: string;
  jawatan: string;
  Bahcaw2: BahcawJanm;
}

interface LampiranMeta {
  id: string;
  nama: string;
  nama_asal: string;
  format: string;
  saiz: string;
  url: string;
}

interface Pengguna {
  __typename: "Pengguna";
  nama_penuh: string;
}

// Updated interface to reflect the structure of the API response
interface PerjawatanTrans {
  __typename: "PerjawatanTrans";
  id: string;
  jenis_transaksi: string;
  jenis_transaksi2: string;
  perjawatan: Perjawatan;
  no_rujukan_surat: string;
  tarikh_kuat_kuasa: string;
  is_gunasama: '1' | '0' | string; // Use string as per your JSON example
  catatan_urusetia: string;
  lampiran_meta: LampiranMeta[];
  created_at: string;
  pengguna: Pengguna;
  bil_changes: string;
}


export default defineComponent({
  components: {
    KTDatatableBackend,
  },
  setup() {
    // Corrected the type to match the backend response structure
    const allTransaksiList = ref<PerjawatanTrans[]>([]);
    const loading = ref(false);

    // Table header configuration
    const headerConfig = ref([
      // Note: key here refers to the column to sort/bind, but we use slots for complex display
      { name: "Jenis Transaksi", key: "jenis_transaksi", sortable: true },
      // { name: "Butiran Perjawatan", key: "id", sortable: true, minWidth: "250px" }, // Using a nested key for a default sort if possible, but slots handle display
      { name: "Kumpulan Perjawatan", key: "perjawatan_kumpulan", sortable: true },
      { name: "Skim", key: "perjawatan_skim", sortable: true },
      { name: "Jawatan", key: "perjawatan_jawatan", sortable: true },
      { name: "Bahagian", key: "perjawatan_bahagian", sortable: true },
      { name: "No Rujukan", key: "no_rujukan_surat", sortable: true },
      { name: "Tarikh Kuat Kuasa", key: "tarikh_kuat_kuasa", sortable: true },
      // { name: "Gunasama", key: "is_gunasama", sortable: true },
      { name: "Catatan", key: "catatan_urusetia", sortable: false },
      { name: "Lampiran", key: "lampiran_meta", sortable: false },
      // { name: "Masa Simpan/Penyimpan", key: "created_at", sortable: true, minWidth: "150px" },
      { name: "Masa Simpan", key: "created_at", sortable: true, minWidth: "150px" },
    ]);

    // Filters
    const filters = reactive<{
      status: string;
    }>({
      status: "",
    });

    // Pagination and sorting (client-side)
    const pagination = reactive({
      page: 1,
      limit: 10,
      sortBy: "id", // Changed to match actual key in PerjawatanTrans
      sortOrder: "desc",
    });

    // Build where clause for GraphQL
    const buildWhereClause = () => {
      const where: any = {};
      
      // Assuming 'status' filter is correctly implemented in your backend GraphQL query.
      // Based on the provided data, a direct 'status' field doesn't seem available, 
      // but 'is_gunasama' ('1'/'0') might be what 'status' is intended for, or a different field.
      // Keeping the original logic for 'status' filter for now.
      if (filters.status !== "") {
        // You may need to map this 'status' to a meaningful field in your backend
        // For example, if 'status' means 'is_gunasama'
        // where.is_gunasama = filters.status;
        console.warn("Status filter may need adjustment based on backend schema.");
      }
      
      return Object.keys(where).length > 0 ? where : null;
    };

    // GraphQL Query
    const { result, loading: queryLoading, refetch, load } = useLazyQuery(
      perjawatanTransList,
      () => ({
        where: buildWhereClause(),
      }),
      { fetchPolicy: "network-only" }
    );

    // Computed: Sorted and filtered data
    const sortedData = computed(() => {
      if (!allTransaksiList.value || allTransaksiList.value.length === 0) {
        return [];
      }
      // If sorting by id, convert to number
      if (pagination.sortBy === 'id') {
        return orderBy(
          allTransaksiList.value,
          [(item) => Number(item.id)],  // <- Convert string to number
          [pagination.sortOrder as 'asc' | 'desc']
        );
      }
      
      // Otherwise sort normally
      return orderBy(
        allTransaksiList.value,
        [pagination.sortBy],
        [pagination.sortOrder as 'asc' | 'desc']
      );
    });

    // Computed: Paginated data
    const paginatedData = computed(() => {
      const start = (pagination.page - 1) * pagination.limit;
      const end = start + pagination.limit;
      return sortedData.value.slice(start, end);
    });

    // Computed: Total items
    const totalItems = computed(() => {
      return sortedData.value.length;
    });

    // Utility for formatting
    const formatDate = (date: string | null | undefined) => 
      date ? moment(date).format("DD/MM/YYYY") : "-";

    const formatDateTime = (date: string | null | undefined) => 
      date ? moment(date).format("DD/MM/YYYY hh:mm A") : "-";

    const formatJenisTransaksi = (jenis: string, jenis2: string) => {
      if (jenis === 'waran_perjawatan') {
        if (jenis2 === 'Tambah') return 'Perjawatan Baharu';
        if (jenis2 === 'Mansuh') return 'Pemansuhan Perjawatan';
      }
      
      if (jenis === 'kemaskini_perjawatan') {
        if (jenis2 === 'Tambah') return 'Penambahan Jawatan';
        if (jenis2 === 'Mansuh') return 'Pengurangan Jawatan';
      }
      
      return jenis2 || '-';
    };
    
    const formatJenisTransaksi2 = (jenis: string) => {
      if (jenis === 'waran_perjawatan') return 'Waran Perjawatan';
      if (jenis === 'kemaskini_perjawatan') return 'Kemaskini Perjawatan';
      return jenis || '-';
    };

    // Fetch data function
    const fetchData = async () => {
      try {
        loading.value = true;
        // Reset page to 1 on any refetch/filter/sort action that changes the dataset
        pagination.page = 1; 
        await refetch({
          where: buildWhereClause(),
        });
      } catch (error) {
        console.error("❌ Error fetching perjawatan transaksi:", error);
      } finally {
        loading.value = false;
      }
    };

    // Event handlers
    const handlePageChange = (newPage: number) => {
      if (pagination.page !== newPage) {
        pagination.page = newPage;
      }
    };

    const handleSortChange = ({ columnName, order }: { columnName: string; order: string }) => {
      pagination.sortBy = columnName;
      pagination.sortOrder = order.toLowerCase();
      pagination.page = 1; // Reset to first page on sort
    };

    const applyFilters = () => {
      fetchData(); // Refetch with new filter (which resets page to 1 via fetchData)
    };

    // Lifecycle hooks
    onMounted(async () => {
      await load();
      setCurrentPageBreadcrumbs("Transaksi Perjawatan", [""]);
      MenuComponent.reinitialization();
    });

    // Watch for changes in the GraphQL result
    watch(result, (res) => {
      const list = res?.perjawatanTransList || [];
      allTransaksiList.value = list;
    });


    // Initial load, in case load() above does not trigger refetch immediately
    // fetchData(); 

    return {
      paginatedData,
      loading,
      totalItems,
      headerConfig,
      filters,
      pagination,
      fetchData,
      applyFilters,
      handlePageChange,
      handleSortChange,
      formatDate,
      formatDateTime,
      formatJenisTransaksi,
      formatJenisTransaksi2
    };
  },
});
